import { RedeemCode, RedeemCodeResponse } from 'mytours-read-api-typescript'
import { reducerWithInitialState } from 'typescript-fsa-reducers/dist'

import * as apiActions from '../api/actions'
import { isHttpClientError } from '../api/errors'
import { expireAtFromHours } from '../api/functions'
import { OwnedCollectionEntry, OwnedCollections } from './types'

// Upsert that bails without a new state reference when the entry is unchanged — boot/sync
// revalidation re-dispatches for every persisted code, and pointless new references would
// re-render the Purchased Content surfaces on every periodic sync.
const setOwnership = (state: StoreState, id: number, entry: OwnedCollectionEntry): StoreState => {
	const prev = state.ownedCollections[id]
	if (
		prev &&
		prev.addedAt === entry.addedAt &&
		prev.expireAt === entry.expireAt &&
		prev.couponUsedToUnlock === entry.couponUsedToUnlock
	) {
		return state
	}
	return { ...state, ownedCollections: { ...state.ownedCollections, [id]: entry } }
}

// A collection can have several persisted codes over time (e.g. a replacement code issued after
// the original was revoked); only the coupon recorded on the entry may revoke it, so a stale
// code's Invalid result can't drop ownership established by a newer valid one.
const dropOwnership = (state: StoreState, id: number, coupon?: string): StoreState => {
	const prev = state.ownedCollections[id]
	if (!prev || (coupon && prev.couponUsedToUnlock && prev.couponUsedToUnlock !== coupon)) {
		return state
	}
	const next = { ...state.ownedCollections }
	delete next[id]
	return { ...state, ownedCollections: next }
}

export interface StoreState {
	readonly ownedCollections: OwnedCollections
}

export const INITIAL_STATE: StoreState = {
	ownedCollections: {},
}

export const reducer = reducerWithInitialState(INITIAL_STATE)
	// Reflect an in-session redemption immediately so a just-redeemed collection appears in
	// Purchased Content without waiting for the next boot/sync.
	.case(apiActions.redeemCode.done, (state, payload): StoreState => {
		const { linkedType, linkedId, couponCode } = payload.params
		if (
			linkedType !== RedeemCode.LinkedTypeEnum.Collection ||
			payload.result.status !== RedeemCodeResponse.StatusEnum.Valid
		) {
			return state
		}
		const prev = state.ownedCollections[linkedId]
		return setOwnership(state, linkedId, {
			addedAt: prev?.addedAt ?? Date.now(),
			expireAt: expireAtFromHours(payload.result.expire_after),
			couponUsedToUnlock: couponCode,
		})
	})
	// The lazy per-collection validation (on opening a collection) drops the entry on a definitive
	// rejection — Invalid/Expired result or a 4xx response. A transport failure or 5xx (offline,
	// backend outage) keeps the optimistic unlock.
	.case(apiActions.validateCode.done, (state, payload): StoreState => {
		const { linkedType, linkedId, couponCode } = payload.params
		const { status } = payload.result
		if (
			linkedType !== RedeemCode.LinkedTypeEnum.Collection ||
			(status !== RedeemCodeResponse.StatusEnum.Invalid && status !== RedeemCodeResponse.StatusEnum.Expired)
		) {
			return state
		}
		return dropOwnership(state, linkedId, couponCode)
	})
	.case(apiActions.validateCode.failed, (state, payload): StoreState => {
		if (payload.params.linkedType !== RedeemCode.LinkedTypeEnum.Collection || !isHttpClientError(payload.error)) {
			return state
		}
		return dropOwnership(state, payload.params.linkedId, payload.params.couponCode)
	})
	// Boot/sync revalidation of a persisted code. `.started` seeds a missing entry optimistically:
	// the action is only ever dispatched for codes that were successfully redeemed and saved to
	// disk, and the slice is wiped on every reload (blacklisted from persistence) — so without
	// seeding, a non-definitive `.done` (e.g. RequiresEmail on a no-email revalidate) or an
	// offline `.failed` would have nothing to preserve and the collection would vanish from
	// Purchased Content. An existing entry is left untouched: like bundles, no dispatch ordering
	// reliably identifies the newest of several codes, so the recorded coupon only changes on a
	// Valid result, and only a result for the recorded coupon may revoke.
	.case(apiActions.validateOwnedCollection.started, (state, payload): StoreState => {
		if (state.ownedCollections[payload.id]) {
			return state
		}
		return setOwnership(state, payload.id, {
			addedAt: payload.addedAt,
			expireAt: payload.expireAt,
			couponUsedToUnlock: payload.coupon,
		})
	})
	.case(apiActions.validateOwnedCollection.done, (state, payload): StoreState => {
		const prev = state.ownedCollections[payload.params.id]
		const { status } = payload.result
		// Like bundles, MaxRedemptions counts as valid when re-validating: the code already
		// unlocked once. A Valid result always (re-)establishes ownership and records its
		// coupon — see the bundles reducer for the race analysis.
		if (status === RedeemCodeResponse.StatusEnum.Valid || status === RedeemCodeResponse.StatusEnum.MaxRedemptions) {
			return setOwnership(state, payload.params.id, {
				addedAt: payload.params.addedAt ?? prev?.addedAt,
				expireAt: payload.params.expireAt,
				couponUsedToUnlock: payload.params.coupon,
			})
		}
		if (status === RedeemCodeResponse.StatusEnum.Invalid || status === RedeemCodeResponse.StatusEnum.Expired) {
			// Definitive negative → no longer owned.
			return dropOwnership(state, payload.params.id, payload.params.coupon)
		}
		// Non-definitive (e.g. RequiresEmail on a no-email revalidate) → preserve prior ownership.
		return state
	})
	.case(apiActions.validateOwnedCollection.failed, (state, payload): StoreState => {
		// Drop only on a definitive 4xx rejection (e.g. revoked/deleted); a transport failure or
		// 5xx keeps the optimistic unlock. Mirrors bundles.
		if (!isHttpClientError(payload.error)) {
			return state
		}
		return dropOwnership(state, payload.params.id, payload.params.coupon)
	})
